import { useEffect, useMemo, useState } from 'react';
import { Activity, AlertTriangle, Bot, Gauge, HeartPulse, ScrollText } from 'lucide-react';
import { Card, Button } from '@/design-system';
import type { AdminRange, ErrorDisposition } from '@/domain/admin/contracts';
import { navigate, useCurrentSearch } from '@/app/router';
import { localizeSystemValue } from '@/app/localization';
import { useAuthStore } from '@/features/auth/state/auth-store';
import {
  getAdminErrorDetail,
  getAdminRequestDetail,
  getAdminSection,
  updateErrorDisposition,
  type AdminSection,
} from './api/admin-api';

const sections: Array<{ id: AdminSection; label: string; icon: typeof Gauge }> = [
  { id: 'overview', label: 'Visão geral', icon: Gauge },
  { id: 'usage', label: 'Uso', icon: Activity },
  { id: 'ai', label: 'Uso de IA', icon: Bot },
  { id: 'errors', label: 'Erros', icon: AlertTriangle },
  { id: 'requests', label: 'Requisições / Logs', icon: ScrollText },
  { id: 'health', label: 'Saúde do sistema', icon: HeartPulse },
];
const ranges: AdminRange[] = ['24h', '7d', '30d'];
type Data = Record<string, unknown>;

function asRecord(value: unknown): Data {
  return value && typeof value === 'object' ? (value as Data) : {};
}
function asList(value: unknown): Data[] {
  return Array.isArray(value) ? value.map(asRecord) : [];
}
function text(value: unknown): string {
  return localizeSystemValue(value);
}
function cellText(key: string, value: unknown, timeZone?: string): string {
  if (typeof value === 'string' && /(At|Seen)$/.test(key) && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'short',
      timeStyle: 'medium',
      timeZone,
    }).format(new Date(value));
  }
  return text(value);
}
function pct(value: unknown): string {
  return typeof value === 'number' ? `${(value * 100).toFixed(1)}%` : 'Desconhecido';
}

function Metric({ label, value, hint }: { label: string; value: unknown; hint?: string }) {
  return (
    <Card>
      <p className="text-xs uppercase tracking-wide text-text-muted">{label}</p>
      <p className="mt-2 text-2xl font-semibold text-text-primary">{text(value)}</p>
      {hint && <p className="mt-1 text-xs text-text-muted">{hint}</p>}
    </Card>
  );
}

function TrendBars({ title, rows, summary }: { title: string; rows: Data[]; summary: string }) {
  const maximum = Math.max(1, ...rows.map((row) => Number(row.value) || 0));
  return (
    <Card>
      <h3 className="font-medium text-text-primary">{title}</h3>
      <div className="mt-4 flex h-28 items-end gap-2" role="img" aria-label={summary}>
        {rows.length ? (
          rows.map((row) => {
            const value = Number(row.value) || 0;
            return (
              <div key={text(row.date)} className="flex min-w-0 flex-1 flex-col items-center gap-1">
                <span className="text-[10px] text-text-muted">{value}</span>
                <span
                  className="w-full rounded-t-sm bg-accent/70"
                  style={{ height: `${Math.max(4, (value / maximum) * 80)}px` }}
                />
                <span className="truncate text-[9px] text-text-muted">
                  {text(row.date).slice(5)}
                </span>
              </div>
            );
          })
        ) : (
          <p className="self-center text-sm text-text-muted">Não há dados de tendência com evidências.</p>
        )}
      </div>
      <p className="mt-3 text-sm text-text-secondary">{summary}</p>
    </Card>
  );
}

function AccessibleTable({
  caption,
  columns,
  rows,
  onRow,
  timeZone,
}: {
  caption: string;
  columns: Array<[string, string]>;
  rows: Data[];
  onRow?: (row: Data) => void;
  timeZone?: string;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full min-w-[680px] text-left text-sm">
        <caption className="sr-only">{caption}</caption>
        <thead className="bg-surface-2 text-text-muted">
          <tr>
            {columns.map(([key, label]) => (
              <th key={key} scope="col" className="px-3 py-2 font-medium">
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length ? (
            rows.map((row, index) => (
              <tr
                key={text(row.id ?? row.fingerprint ?? index)}
                className={`border-t border-border-subtle ${onRow ? 'cursor-pointer hover:bg-surface-2' : ''}`}
                tabIndex={onRow ? 0 : undefined}
                onClick={() => onRow?.(row)}
                onKeyDown={(event) => {
                  if (onRow && (event.key === 'Enter' || event.key === ' ')) onRow(row);
                }}
              >
                {columns.map(([key]) => (
                  <td key={key} className="max-w-xs truncate px-3 py-2 text-text-secondary">
                    {cellText(key, row[key], timeZone)}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={columns.length} className="px-3 py-8 text-center text-text-muted">
                Não há dados com evidências neste período.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export function AdminPage() {
  const timeZone = useAuthStore((state) => state.user?.timezone ?? undefined);
  const displayTimeZone = timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const rawSearch = useCurrentSearch();
  const params = useMemo(() => new URLSearchParams(rawSearch), [rawSearch]);
  const section = (
    sections.some((item) => item.id === params.get('section')) ? params.get('section') : 'overview'
  ) as AdminSection;
  const range = (
    ranges.includes(params.get('range') as AdminRange) ? params.get('range') : '24h'
  ) as AdminRange;
  const query = params.get('q') ?? '';
  const severity = params.get('severity') ?? '';
  const disposition = params.get('status') ?? '';
  const errorClass = params.get('class') ?? '';
  const errorCode = params.get('code') ?? '';
  const routeFilter = params.get('route') ?? '';
  const operationFilter = params.get('operation') ?? '';
  const httpStatus = params.get('httpStatus') ?? '';
  const slowOnly = params.get('slow') === 'true';
  const [data, setData] = useState<Data | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<Data | null>(null);
  const [search, setSearch] = useState(query);
  const suffix =
    section === 'errors'
      ? `&search=${encodeURIComponent(query)}&severity=${encodeURIComponent(severity)}&status=${encodeURIComponent(disposition)}&class=${encodeURIComponent(errorClass)}&code=${encodeURIComponent(errorCode)}&route=${encodeURIComponent(routeFilter)}&operation=${encodeURIComponent(operationFilter)}&httpStatus=${encodeURIComponent(httpStatus)}`
      : section === 'requests'
        ? `&search=${encodeURIComponent(query)}&slow=${slowOnly}&status=${encodeURIComponent(httpStatus)}&route=${encodeURIComponent(routeFilter)}&operation=${encodeURIComponent(operationFilter)}`
        : '';

  useEffect(() => {
    setData(null);
    setError(null);
    setDetail(null);
    getAdminSection(section, range, suffix)
      .then(setData)
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : 'Não foi possível carregar os dados administrativos.'),
      );
  }, [section, range, suffix]);
  function setParams(next: Record<string, string | undefined>) {
    const copy = new URLSearchParams(params);
    Object.entries(next).forEach(([key, value]) => {
      if (value) copy.set(key, value);
      else copy.delete(key);
    });
    navigate(`/app/admin?${copy}`);
  }

  const openDetail = async (row: Data) => {
    try {
      setDetail(
        section === 'errors'
          ? await getAdminErrorDetail(text(row.fingerprint))
          : await getAdminRequestDetail(text(row.requestId)),
      );
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Detalhe indisponível.');
    }
  };
  const updateStatus = async (status: ErrorDisposition) => {
    const fingerprint = detail?.fingerprint;
    if (typeof fingerprint !== 'string') return;
    await updateErrorDisposition(fingerprint, status, null);
    setDetail(null);
    setData(await getAdminSection('errors', range, suffix));
  };
  const relatedFunnelId = detail
    ? asList(detail.events).find((event) => typeof event.funnelId === 'string')?.funnelId
    : undefined;

  return (
    <main className="mx-auto w-full max-w-[1440px] px-4 py-6 lg:px-8">
      <div className="mb-6">
        <p className="text-xs font-medium uppercase tracking-[0.16em] text-accent">Operações</p>
        <h1 className="mt-1 text-2xl font-semibold text-text-primary">Administração</h1>
        <p className="mt-1 text-sm text-text-muted">
          Uso, confiabilidade e evidências operacionais seguras. Sem faturamento ou gestão de usuários.
        </p>
        <p className="mt-1 text-xs text-text-muted">
          Horários exibidos em {displayTimeZone}; os registros persistidos permanecem em UTC.
        </p>
      </div>
      <div className="grid gap-6 lg:grid-cols-[220px_1fr]">
        <nav aria-label="Seções administrativas" className="flex gap-2 overflow-x-auto lg:flex-col">
          {sections.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setParams({ section: id })}
              aria-current={section === id ? 'page' : undefined}
              className={`flex shrink-0 items-center gap-2 rounded-md px-3 py-2 text-left text-sm ${section === id ? 'bg-[rgba(212,168,83,.14)] text-accent' : 'text-text-secondary hover:bg-surface'}`}
            >
              <Icon size={16} />
              {label}
            </button>
          ))}
        </nav>
        <section>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-text-primary">
              {sections.find((item) => item.id === section)?.label}
            </h2>
            <div className="flex rounded-md border border-border p-1" aria-label="Período">
              {ranges.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setParams({ range: item })}
                  aria-pressed={range === item}
                  className={`rounded px-3 py-1 text-xs ${range === item ? 'bg-accent text-[#070807]' : 'text-text-muted'}`}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
          {(section === 'errors' || section === 'requests') && (
            <form
              className="mb-4 flex flex-wrap gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                setParams({ q: search });
              }}
            >
              <label className="sr-only" htmlFor="admin-search">
                Buscar ID da requisição, código, rota ou operação
              </label>
              <input
                id="admin-search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar ID da requisição, código, rota ou operação"
                className="min-w-[240px] flex-1 rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary"
              />
              {section === 'errors' && (
                <>
                  <label className="sr-only" htmlFor="admin-severity">
                    Severidade
                  </label>
                  <select
                    id="admin-severity"
                    value={severity}
                    onChange={(event) => setParams({ severity: event.target.value || undefined })}
                    className="rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary"
                  >
                    <option value="">Todas as severidades</option>
                    <option value="info">Informação</option>
                    <option value="warning">Aviso</option>
                    <option value="error">Erro</option>
                    <option value="critical">Crítico</option>
                  </select>
                  <label className="sr-only" htmlFor="admin-status">
                    Situação
                  </label>
                  <select
                    id="admin-status"
                    value={disposition}
                    onChange={(event) => setParams({ status: event.target.value || undefined })}
                    className="rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary"
                  >
                    <option value="">Todos os status</option>
                    <option value="open">Aberto</option>
                    <option value="acknowledged">Reconhecido</option>
                    <option value="resolved">Resolvido</option>
                    <option value="ignored">Ignorado</option>
                  </select>
                  <label className="sr-only" htmlFor="admin-error-class">
                    Classe do erro
                  </label>
                  <select
                    id="admin-error-class"
                    value={errorClass}
                    onChange={(event) => setParams({ class: event.target.value || undefined })}
                    className="rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary"
                  >
                    <option value="">Todas as classes</option>
                    {[
                      'validation',
                      'auth',
                      'permission',
                      'not_found',
                      'conflict',
                      'rate_limit',
                      'dependency',
                      'timeout',
                      'database',
                      'storage',
                      'ai_provider',
                      'internal',
                      'unknown',
                    ].map((item) => (
                      <option key={item} value={item}>
                        {localizeSystemValue(item)}
                      </option>
                    ))}
                  </select>
                  <label className="sr-only" htmlFor="admin-error-code">
                    Código do erro
                  </label>
                  <input
                    id="admin-error-code"
                    value={errorCode}
                    onChange={(event) => setParams({ code: event.target.value || undefined })}
                    placeholder="Código do erro"
                    className="w-36 rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary"
                  />
                </>
              )}
              <label className="sr-only" htmlFor="admin-http-status">
                Status HTTP
              </label>
              <select
                id="admin-http-status"
                value={httpStatus}
                onChange={(event) => setParams({ httpStatus: event.target.value || undefined })}
                className="rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary"
              >
                <option value="">Todos os status HTTP</option>
                <option value="200">200</option>
                <option value="400">400</option>
                <option value="401">401</option>
                <option value="403">403</option>
                <option value="404">404</option>
                <option value="409">409</option>
                <option value="429">429</option>
                <option value="500">500</option>
                <option value="503">503</option>
              </select>
              {section === 'requests' && (
                <>
                  <label className="flex items-center gap-2 rounded-md border border-border bg-surface px-3 text-sm text-text-secondary">
                    <input
                      type="checkbox"
                      checked={slowOnly}
                      onChange={(event) =>
                        setParams({ slow: event.target.checked ? 'true' : undefined })
                      }
                    />
                    Apenas lentas
                  </label>
                </>
              )}
              <label className="sr-only" htmlFor="admin-route-filter">
                Rota
              </label>
              <input
                id="admin-route-filter"
                value={routeFilter}
                onChange={(event) => setParams({ route: event.target.value || undefined })}
                placeholder="Rota"
                className="w-40 rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary"
              />
              <label className="sr-only" htmlFor="admin-operation-filter">
                Operação
              </label>
              <input
                id="admin-operation-filter"
                value={operationFilter}
                onChange={(event) => setParams({ operation: event.target.value || undefined })}
                placeholder="Operação"
                className="w-40 rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary"
              />
              <Button variant="secondary" type="submit">
                Filtrar
              </Button>
            </form>
          )}
          {error ? (
            <Card role="alert" className="border-danger text-danger">
              {error}
            </Card>
          ) : !data ? (
            <Card aria-live="polite" className="text-text-muted">
              Carregando dados operacionais com evidências…
            </Card>
          ) : (
            <AdminContent
              section={section}
              data={data}
              openDetail={openDetail}
              timeZone={timeZone}
            />
          )}
        </section>
      </div>
      {detail && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Detalhe operacional"
          className="fixed inset-0 z-30 flex items-end justify-end bg-black/60"
        >
          <div className="h-full w-full max-w-2xl overflow-auto border-l border-border bg-bg-app p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-text-primary">Detalhe operacional seguro</h2>
              <Button variant="secondary" onClick={() => setDetail(null)}>
                Fechar
              </Button>
            </div>
            <pre className="whitespace-pre-wrap break-words rounded-md bg-surface p-4 text-xs text-text-secondary">
              {JSON.stringify(detail, null, 2)}
            </pre>
            <div className="mt-4 flex flex-wrap gap-2">
              {typeof relatedFunnelId === 'string' && (
                <Button
                  variant="secondary"
                  onClick={() => navigate(`/app/funnels/${relatedFunnelId}`)}
                >
                  Abrir funil relacionado
                </Button>
              )}
              {section === 'errors' &&
                (['open', 'acknowledged', 'resolved', 'ignored'] as ErrorDisposition[]).map(
                  (status) => (
                    <Button
                      key={status}
                      variant="secondary"
                      onClick={() => void updateStatus(status)}
                    >
                      {localizeSystemValue(status)}
                    </Button>
                  ),
                )}
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

function AdminContent({
  section,
  data,
  openDetail,
  timeZone,
}: {
  section: AdminSection;
  data: Data;
  openDetail: (row: Data) => void;
  timeZone?: string;
}) {
  if (section === 'overview') {
    const usage = asRecord(data.usage),
      features = asRecord(usage.featureTotals),
      errors = asRecord(data.errors),
      requests = asRecord(data.requests),
      ai = asRecord(data.ai),
      health = asRecord(data.health);
    return (
      <div className="space-y-5">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Metric label="Saúde do sistema" value={health.status} hint="Derivada de verificações explícitas" />
          <Metric
            label="Funis ativos"
            value={asRecord(features.activeFunnels).current}
            hint="Funis com atividade qualificadora"
          />
          <Metric label="Funis criados" value={asRecord(features.funnelsCreated).current} />
          <Metric label="Visualizações de compartilhamento" value={asRecord(features.shareViews).current} />
          <Metric label="Requisições de IA" value={ai.requests} />
          <Metric label="Quantidade de erros" value={errors.count} />
          <Metric label="5xx rate" value={pct(requests.serverErrorRate)} />
          <Metric
            label="Latência P95"
            value={requests.p95Ms === null ? 'Desconhecido' : `${text(requests.p95Ms)} ms`}
          />
        </div>
        <Card>
          <h3 className="font-medium text-text-primary">Requer atenção</h3>
          <ul className="mt-2 space-y-1 text-sm text-text-secondary">
            {Array.isArray(data.needsAttention) && data.needsAttention.length ? (
              data.needsAttention.map((item, i) => <li key={i}>• {text(item)}</li>)
            ) : (
              <li>Nenhum sinal elevado foi detectado nos dados com evidências.</li>
            )}
          </ul>
        </Card>
        <div className="grid gap-4 xl:grid-cols-2">
          <Card>
            <h3 className="font-medium text-text-primary">Erros críticos abertos</h3>
            <ul className="mt-2 space-y-2 text-sm text-text-secondary">
              {asList(errors.openCritical).length ? (
                asList(errors.openCritical).map((item) => (
                  <li key={text(item.fingerprint)}>
                    {text(item.errorCode)} · {text(item.route)}
                  </li>
                ))
              ) : (
                <li>Não há grupo crítico aberto neste período.</li>
              )}
            </ul>
          </Card>
          <Card>
            <h3 className="font-medium text-text-primary">Requisições com falha recentes</h3>
            <ul className="mt-2 space-y-2 text-sm text-text-secondary">
              {asList(requests.recentFailed).length ? (
                asList(requests.recentFailed).map((item) => (
                  <li key={text(item.requestId)}>
                    {text(item.statusCode)} · {text(item.route)} ·{' '}
                    {cellText('createdAt', item.createdAt, timeZone)}
                  </li>
                ))
              ) : (
                <li>Não há requisições com falha neste período.</li>
              )}
            </ul>
          </Card>
        </div>
      </div>
    );
  }
  if (section === 'usage') {
    const totals = asRecord(data.featureTotals),
      users = asRecord(data.users),
      totalUsers = asRecord(users.totalUsers),
      activeUsers = asRecord(users.activeUsers);
    return (
      <div className="space-y-5">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Metric label="Total de usuários" value={totalUsers.value} hint={text(totalUsers.evidence)} />
          <Metric
            label="Usuários ativos"
            value={activeUsers.value}
            hint={text(activeUsers.evidence)}
          />
          {Object.entries(totals).map(([key, value]) => (
            <Metric
              key={key}
              label={key}
              value={asRecord(value).current}
              hint={`Anterior: ${text(asRecord(value).previous)}`}
            />
          ))}
        </div>
        <Card>
          <p className="text-sm text-text-secondary">
            {text(data.trendSummary)} Apenas comparação histórica; nenhuma previsão é gerada.
          </p>
        </Card>
        <TrendBars
          title="Tendência de atividade do produto"
          rows={asList(data.trend)}
          summary={`${text(data.trendSummary)} Apenas comparação histórica; nenhuma previsão é gerada.`}
        />
      </div>
    );
  }
  if (section === 'ai')
    return (
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Metric label="Requisições" value={data.requests} />
          <Metric label="Concluídas" value={data.succeeded} />
          <Metric label="Com falha" value={data.failed} />
          <Metric label="Taxa de falha" value={pct(data.failureRate)} />
          <Metric
            label="Latência média"
            value={data.averageLatencyMs === null ? 'Desconhecido' : `${text(data.averageLatencyMs)} ms`}
          />
          <Metric label="Unidades de entrada" value={data.inputUnits} />
          <Metric label="Unidades de saída" value={data.outputUnits} />
          <Metric label="Limitadas por taxa" value={data.rateLimited} />
          <Metric label="Bloqueadas por cota" value={data.quotaBlocked} />
          <Metric
            label="Custo estimado"
            value="Indisponível"
            hint="Não há metadados confiáveis de preço; nenhum custo foi inventado."
          />
        </div>
        <div className="grid gap-4 xl:grid-cols-2">
          <AccessibleTable
            caption="Uso de IA por operação"
            columns={[
              ['name', 'Operação'],
              ['count', 'Requisições'],
            ]}
            rows={asList(data.operationSplit)}
          />
          <AccessibleTable
            caption="Uso de IA por classe de modelo"
            columns={[
              ['name', 'Classe de modelo'],
              ['count', 'Requisições'],
            ]}
            rows={asList(data.modelClassSplit)}
          />
        </div>
      </div>
    );
  if (section === 'errors')
    return (
      <div className="space-y-4">
        <Card>
          <p className="text-sm text-text-secondary">{text(data.trendSummary)}</p>
        </Card>
        <TrendBars
          title="Tendência de erros"
          rows={asList(data.trend)}
          summary={text(data.trendSummary)}
        />
        <AccessibleTable
          caption="Erros agrupados da aplicação"
          columns={[
            ['fingerprint', 'Fingerprint'],
            ['errorCode', 'Código'],
            ['severity', 'Severidade'],
            ['disposition', 'Status'],
            ['count', 'Quantidade'],
            ['lastSeen', 'Visto por último'],
            ['route', 'Route'],
          ]}
          rows={asList(data.items)}
          onRow={openDetail}
          timeZone={timeZone}
        />
      </div>
    );
  if (section === 'requests') {
    const stats = asRecord(data.stats);
    return (
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Metric label="Requisições" value={stats.count} />
          <Metric
            label="Média"
            value={stats.averageMs === null ? 'Desconhecido' : `${text(stats.averageMs)} ms`}
          />
          <Metric
            label="P95"
            value={stats.p95Ms === null ? 'Desconhecido' : `${text(stats.p95Ms)} ms`}
          />
          <Metric label="5xx rate" value={pct(stats.serverErrorRate)} />
        </div>
        <AccessibleTable
          caption="Metadados das requisições de API"
          columns={[
            ['requestId', 'Request ID'],
            ['method', 'Método'],
            ['route', 'Route'],
            ['statusCode', 'Status'],
            ['durationMs', 'Duração (ms)'],
            ['createdAt', 'Horário'],
          ]}
          rows={asList(data.items)}
          onRow={openDetail}
          timeZone={timeZone}
        />
      </div>
    );
  }
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {asList(data.signals).map((signal) => (
        <Metric
          key={text(signal.dependency)}
          label={text(signal.dependency)}
          value={signal.status}
          hint={`${text(signal.detail)} ${signal.latencyMs !== undefined ? `(${text(signal.latencyMs)} ms)` : ''}`}
        />
      ))}
    </div>
  );
}
